import { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  useGetCampaignDetailsQuery,
  usePatchCampaignMembersMutation,
} from "api/companyApi";
import { useParams } from "react-router-dom";
import { AnalysisStatus, MembersUpdateValues } from "types/companyTypes";
import { ExportAccountDataBtn } from "../../ui/export-data";
import { PenIcon } from "../../images";
import { EllipsisTooltip } from "components/ui/CustomTooltip";
import { UpdateExternalDataBtn } from "../../features";
import { DiscardModal } from "./DiscardModal";
import { SaveModal } from "./SaveModal";
import { SliderBlock } from "./SliderBlock";
import { AccountFiltersDropdown } from "../../ui/dropdown";
import { toast } from "react-toastify";
import { CustomToast } from "../../ui/CustomToast";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { setDiscardTableChanges } from "../../../features/companySlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { OpenModalButton } from "../../ui/buttons";
import { GeneratePlaybooksModal } from "../../ui/modal";
import { selectPermissions } from "../../../features/permissionsSlice";
import { ReuseButton } from "../../ui";
import {
  ButtonsColorType,
  ButtonsType,
  CampaignPurpose,
  PlaybookPlanNames,
} from "../../../types/enums";

export const MembersActionBlock = ({
  isEditMode,
  setIsEditMode,
  page,
  setPage,
  updatedValues,
  committeeSelected,
  clusterSelected,
  onDiscard,
  onSaveChanges,
  personas,
}: {
  isEditMode: boolean;
  committeeSelected: number;
  clusterSelected: number;
  setIsEditMode: (isEditMode: boolean) => void;
  onDiscard: () => void;
  page: number;
  setPage: (page: number) => void;
  updatedValues: { [key: string]: MembersUpdateValues };
  onSaveChanges: () => void;
  personas: { id: string; name: string; actual_count: number }[]; // ← добавить
}) => {
  const dispatch = useAppDispatch();
  const { account_id, campaign_id } = useParams();
  const { isEditor } = useAppSelector((state) => state.company.userRoles);
  const { isAllowedEditGenerateReports } = useAppSelector(selectPermissions);

  const [updateMembers, { isLoading: updateMembersIsLoading }] =
    usePatchCampaignMembersMutation();

  const [viewTable] = useState<"table" | "chart">("table");

  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const { data } = useGetCampaignDetailsQuery({
    account_id: account_id!,
  });

  const handleDiscardChanges = () => {
    if (Object.values(updatedValues).length === 0) {
      setIsDiscardModalOpen(false);
      setIsEditMode(false);
      return;
    }
    setIsDiscardModalOpen(true);
  };

  const handleSaveChanges = async () => {
    setIsSaveModalOpen(true);
  };

  if (!data) return null;

  const {
    status,
    progress,
    is_analyzed,
    scraping_update_status,
    scraping_status,
    total_members,
    campaign_purpose,
    output: {
      summary: {
        expected: {
          committee_members: committeeExpected,
          cluster_members: clusterExpected,
        },
      },
    },
    total_members_with_playbook,
  } = data;

  const isAnalyze = [AnalysisStatus.queued, AnalysisStatus.in_progress].some(
    (el) => el === status,
  );

  const isScraping = [AnalysisStatus.queued, AnalysisStatus.in_progress].some(
    (el) => el === scraping_status,
  );

  const isScrapingUpdate = [
    AnalysisStatus.queued,
    AnalysisStatus.in_progress,
  ].some((el) => el === scraping_update_status);

  const isSalesCampaign = campaign_purpose === CampaignPurpose.sales;

  const isProcessingDisabled =
    isAnalyze || isScraping || isScrapingUpdate || isSalesCampaign;

  const confirmDiscard = () => {
    setIsDiscardModalOpen(false);
    setIsEditMode(false);
    onDiscard();
    dispatch(setDiscardTableChanges(true));
  };

  const discardCancel = () => {
    setIsDiscardModalOpen(false);
  };

  const saveChangesHandler = () => {
    updateMembers({
      account_id: account_id!,
      campaign_id: campaign_id!,
      updatedValues: Object.values(updatedValues),
    })
      .unwrap()
      .then(() => {
        setIsEditMode(false);
        setIsSaveModalOpen(false);
        onSaveChanges();
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title={"Changes Were Not Saved"}
          />,
        );
      });
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      flexGrow={1}
      alignItems="flex-end"
    >
      {!isEditMode ? (
        <Stack flexDirection="row" gap="12px" alignItems="flex-end">
          <SliderBlock
            total_members={total_members}
            page={page}
            setPage={setPage}
          />
          <AccountFiltersDropdown
            isDisabled={total_members === 0}
            dataSourceList={data.data_source || []}
            personas={personas}
          />
        </Stack>
      ) : (
        <>
          <Box
            display="flex"
            alignItems="center"
            flexDirection="row"
            gap={8}
            paddingBottom="12px"
          >
            <Typography variant="body1" color="text.secondary">
              Selected BC vs. Expected BC Size:
            </Typography>
            <Typography color="custom.textDarkBlue">
              Buying Committee{" "}
              <Typography color="text.secondary" component="span">
                {committeeSelected}/{committeeExpected}
              </Typography>
            </Typography>
            <Typography color="custom.textDarkBlue">
              Buying Cluster{" "}
              <Typography color="text.secondary" component="span">
                {clusterSelected}/{clusterExpected}
              </Typography>
            </Typography>
          </Box>
        </>
      )}

      {viewTable === "table" ? (
        <Stack direction="row" gap="12px" alignItems="flex-end">
          {!isEditMode && (
            <ExportAccountDataBtn
              total_members_with_playbook={total_members_with_playbook}
              account_id={account_id!}
              campaign_id={campaign_id!}
              is_analyzed={is_analyzed}
            />
          )}
          {!isEditMode ? (
            <>
              {isEditor && (
                <EllipsisTooltip
                  tooltip={
                    is_analyzed
                      ? "Edit table"
                      : "You haven’t analyzed this company yet"
                  }
                >
                  <ReuseButton
                    onClick={() => setIsEditMode(true)}
                    disabled={
                      [
                        "in_progress",
                        "queued",
                        "scraping_queued",
                        "scraping_in_progress",
                      ].includes(status) || !is_analyzed
                    }
                    sx={{ paddingX: "6px", minWidth: 0 }}
                  >
                    <PenIcon
                      color={
                        [
                          "in_progress",
                          "queued",
                          "scraping_queued",
                          "scraping_in_progress",
                        ].includes(status) || !is_analyzed
                          ? "#9B9B9B"
                          : undefined
                      }
                    />
                  </ReuseButton>
                </EllipsisTooltip>
              )}
            </>
          ) : (
            <Box display="flex" gap={1}>
              <ReuseButton
                onClick={handleDiscardChanges}
                buttonColor={ButtonsColorType.grey}
                buttonType={ButtonsType.contained}
              >
                Discard Changes
              </ReuseButton>
              <ReuseButton
                onClick={handleSaveChanges}
                buttonColor={ButtonsColorType.blue_white}
                buttonType={ButtonsType.contained}
              >
                Save Changes
              </ReuseButton>
            </Box>
          )}

          {!isEditMode &&
            isEditor &&
            total_members_with_playbook !== total_members && (
              <OpenModalButton
                isDisabled={!isAllowedEditGenerateReports}
                btnText={`Generate ${PlaybookPlanNames.playbooks}`}
                renderModal={({ isOpen, onClose, setIsLoading }) => (
                  <GeneratePlaybooksModal
                    isOpen={isOpen}
                    onClose={onClose}
                    setIsLoading={setIsLoading}
                  />
                )}
              />
            )}

          {!isEditMode && isEditor && (
            <UpdateExternalDataBtn
              progress={progress}
              scrapingUpdateStatus={scraping_update_status}
              account_id={account_id!}
              campaign_id={campaign_id!}
              status={status}
              isDisabled={isProcessingDisabled || !is_analyzed}
            />
          )}
        </Stack>
      ) : (
        <Box mt="auto" display="flex" flexDirection="row" gap={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 24,
                height: 20,
                backgroundColor: "#83AFD3",
              }}
            />
            <Typography variant="body1" color="#1F2932">
              Buying Committee
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 24,
                height: 20,
                backgroundColor: "#0860A8",
              }}
            />
            <Typography variant="body1" color="#1F2932">
              Buying Cluster
            </Typography>
          </Box>
        </Box>
      )}

      {isDiscardModalOpen && (
        <DiscardModal
          isOpen={isDiscardModalOpen}
          onCancelHandler={discardCancel}
          successHandler={confirmDiscard}
        />
      )}

      {isSaveModalOpen && (
        <SaveModal
          isOpen={isSaveModalOpen}
          setIsOpen={setIsSaveModalOpen}
          isLoading={updateMembersIsLoading}
          successHandler={saveChangesHandler}
          clusterExpected={clusterExpected}
          committeeExpected={committeeExpected}
          clusterSelected={clusterSelected}
          committeeSelected={committeeSelected}
        />
      )}
    </Box>
  );
};
